import {
  Button,
  Combobox,
  DatePicker,
  Field,
  FieldLabel,
  LeaderGroupCombobox,
  ModuleHeader,
} from '@/components'
import { useAllIssueTags } from '@/modules/common/hooks/use-all-issue-tags'
import { TagBadge } from '../components/tag-picker/tag-badge'
import { Input } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { PlainLink } from '@/components_deprecated/Link'
import { PermissionsRequired } from '@/containers/permission-containers'
import { PageType } from '@/modules/common/consts'
import { Module } from '@/modules/companies'
import {
  asBoolean,
  asEnumArrayOrEmpty,
  asNullableString,
  asString,
  asStringArrayOrEmpty,
  parseDateRangeOrDefault,
} from '@/utils'
import {
  useAdmin,
  useAutoSelectSingleLeaderGroup,
  useLeaderGroups,
  usePermissions,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { Icon } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, startOfMonth } from 'date-fns'
import React, { useState } from 'react'
import { BrowserView, isMobileOnly, MobileView } from 'react-device-detect'
import styled from 'styled-components'
import { IssuesTable } from '../components'
import { IssuePopupFilter } from '../components/IssuePopupFilter'
import { ISSUE_INVOLVEMENT_FILTERS, ISSUE_STATUS_FILTERS } from '../types'
import type { IssueOrderFields, IssuesTableFilter } from '../types'
import type { IssueSeverity } from '../types.graphl'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};

  ${props => props.theme.layout.largePad} {
    padding: 122px 52px 52px;
    box-sizing: border-box;
  }
`
const Controls = styled(Card)`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
`
const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;
`
const StyledInput = styled(Input)`
  ${isMobileOnly &&
  `
    width: 100%;
  `}
`

const DEFAULT_DATE_RANGE = {
  from: startOfMonth(new Date()),
  to: endOfMonth(new Date()),
}

const ISSUE_ORDER_FIELDS: IssueOrderFields[] = [
  'createdAt',
  '-createdAt',
  'timeOfIssueStart',
  '-timeOfIssueStart',
  'user__firstName',
  '-user__firstName',
  'category__name',
  '-category__name',
  'severity',
  '-severity',
  'riskAspect__name',
  '-riskAspect__name',
]

const ISSUE_SEVERITIES = [
  'LOW',
  'MEDIUM',
  'HIGH',
] as const satisfies readonly IssueSeverity[]

interface IssuesProps {}

export const Issues: React.FC<IssuesProps> = () => {
  const translations = useTranslate({
    issues: 'common.issues',
    createNew: 'common.create',
    findIssue: 'issues.find-issue',
    search: 'common.search-filter',
    period: 'common.period',
    leaderGroup: 'common.leader-group',
    advancedFilters: 'common.advanced-filters',
    chooseGroup: 'common.choose-group',
    amount: 'common.amount',
    date: 'common.date',
    tags: 'common.tags',
  })

  const { tags: allTags } = useAllIssueTags()
  const tagOptions = React.useMemo(
    () =>
      allTags.map(tag => ({
        value: tag.id,
        label: <TagBadge tag={tag} variant="dot" />,
        filterValue: tag.name,
      })),
    [allTags]
  )

  const { hasAccess, myLeaderGroups, groupsLoading } = useLeaderGroups(
    Module.ISSUES
  )
  const hasLeaderGroups = hasAccess || myLeaderGroups.length > 0
  const isAdmin = useAdmin()
  const { hasPermissions } = usePermissions()
  const canViewOthersIssue = hasPermissions(
    PERMISSIONS.issues.view.othersissue,
    true
  )

  const {
    formValues: filter,
    formChangeHandler: filterHandler,
    updateForm,
    loadStoredFormState,
  } = useForm<IssuesTableFilter>({
    values: {
      query: '',
      orderBy: '-timeOfIssueStart',
      excludeCategories: [],
      statuses: [],
      archived: false,
      severities: [],
      onlyManuallyCreated: true,
      involvement: [],
      leaderGroup: null,
      leaderGroupUsers: [],
      tags: [],
      dateRange: DEFAULT_DATE_RANGE,
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'issue-filters',
        sessionStorage: true,
        transformValues: {
          query: asString,
          orderBy: value => {
            const parsed = asString(value)
            if (!parsed) return undefined
            if (ISSUE_ORDER_FIELDS.some(field => field === parsed))
              return parsed as IssueOrderFields
            return undefined
          },
          excludeCategories: asStringArrayOrEmpty,
          statuses: value => asEnumArrayOrEmpty(value, ISSUE_STATUS_FILTERS),
          archived: asBoolean,
          severities: value => asEnumArrayOrEmpty(value, ISSUE_SEVERITIES),
          onlyManuallyCreated: asBoolean,
          involvement: value =>
            asEnumArrayOrEmpty(value, ISSUE_INVOLVEMENT_FILTERS),
          dateRange: parseDateRangeOrDefault,
          leaderGroup: asNullableString,
          leaderGroupUsers: asStringArrayOrEmpty,
          tags: asStringArrayOrEmpty,
        },
      },
    },
  })

  const changeFilter = (value: string | null) => {
    const leaderGroup = value || null
    const selectedGroup = myLeaderGroups.find(g => g.id === leaderGroup)
    updateForm({
      leaderGroup,
      leaderGroupUsers: selectedGroup?.users.map(user => user.id) ?? [],
    })
  }

  const [filteredCount, setFilteredCount] = useState<number | undefined>(
    undefined
  )

  useAutoSelectSingleLeaderGroup({
    canViewOthers: canViewOthersIssue && hasLeaderGroups && !groupsLoading,
    isAdmin,
    leaderGroup: filter.leaderGroup ?? undefined,
    leaderGroups: myLeaderGroups,
    loadStoredFormState,
    updateForm,
    onAutoSelect: group => {
      updateForm({
        leaderGroup: group.id,
        leaderGroupUsers: group.users.map(user => user.id),
      })
    },
  })

  return (
    <Wrapper>
      <ModuleHeader title={translations.issues} page={PageType.ISSUES}>
        <MobileView>
          <PermissionsRequired permissions={PERMISSIONS.issues.add.issue}>
            <Button asChild size="icon">
              <PlainLink to="/issues/create">
                <Icon icon="plus" />
              </PlainLink>
            </Button>
          </PermissionsRequired>
        </MobileView>

        <BrowserView>
          {typeof filteredCount !== 'undefined' && (
            <span className="text-primary">
              {translations.amount}: {filteredCount}
            </span>
          )}
        </BrowserView>
      </ModuleHeader>

      <Controls>
        <ButtonContainer>
          <Field>
            <FieldLabel>{translations.search}</FieldLabel>
            <StyledInput
              value={filter.query}
              type="search"
              placeholder={translations.findIssue}
              onChange={filterHandler('query')}
            />
          </Field>
          <Field>
            <FieldLabel>{translations.period}</FieldLabel>
            <DatePicker
              mode="range"
              value={filter.dateRange}
              format="PP"
              onChange={value => {
                if (!value?.from || !value.to) return
                filterHandler('dateRange')({ from: value.from, to: value.to })
              }}
            />
          </Field>

          {hasLeaderGroups && canViewOthersIssue && (
            <Field>
              <FieldLabel>{translations.leaderGroup}</FieldLabel>
              <LeaderGroupCombobox
                mode="single"
                module={Module.ISSUES}
                value={filter.leaderGroup ?? ''}
                clearable
                placeholder={translations.chooseGroup}
                onChange={value => changeFilter(value || null)}
              />
            </Field>
          )}
          <Field>
            <FieldLabel>{translations.tags}</FieldLabel>
            <Combobox<string>
              mode="multiple"
              value={filter.tags ?? []}
              items={tagOptions}
              placeholder={translations.tags}
              onChange={tags => updateForm({ tags })}
            />
          </Field>
        </ButtonContainer>

        <BrowserView>
          <Field>
            <FieldLabel>{translations.advancedFilters}</FieldLabel>
            <IssuePopupFilter filter={filter} onUpdate={updateForm} />
          </Field>
        </BrowserView>

        <BrowserView className="self-end">
          <PermissionsRequired permissions={PERMISSIONS.issues.add.issue}>
            <Button asChild>
              <PlainLink to="/issues/create">
                <Icon icon="plus" />
                {translations.createNew}
              </PlainLink>
            </Button>
          </PermissionsRequired>
        </BrowserView>
      </Controls>

      <IssuesTable
        filter={filter}
        onSort={orderBy => updateForm({ orderBy })}
        onFilterUpdate={({ count }) => {
          setFilteredCount(count)
        }}
      />
    </Wrapper>
  )
}
